// src/pages/QuizPage/hooks/useFixedQuestionsQuiz.ts
// Bitta so'rovda savollar keladigan, oldindan belgilangan uzunlikdagi
// (lives'siz, timer'siz, session'siz) testlar uchun umumiy hook —
// hozircha "murakkab" va "sonli" rejimlari shu bilan ishlaydi.
import { useState, useEffect, useCallback, useRef } from "react";
import { mapRawQuestions } from "../utils/quizLang";
import { shuffleArray, getShuffleSettings } from "../utils/shuffleUtils";
import type { Question, AnswerResult } from "../utils/quizTypes";

const AUTO_ADVANCE_DELAY = 600;
const AUTO_FINISH_DELAY = 1000;
// "Sonli"/"Murakkab" rejimlari — shu turdagi savollarning HAMMASINI
// ko'rsatishi kerak (20 emas). Hozircha bazadagi eng ko'p turdagi
// savollar soni ~100 atrofida, 500 katta zaxira bilan yetarli.
const DEFAULT_LIMIT = 500;

type UiLang = "oz" | "uz" | "ru";

interface FetchFn {
  (lang: string, limit: number, exclude: number[]): Promise<{ data: any }>;
}

interface UseFixedQuestionsQuizOptions {
  lang: UiLang;
  fetchQuestions: FetchFn;
  limit?: number;
}

export function useFixedQuestionsQuiz({
  lang,
  fetchQuestions,
  limit = DEFAULT_LIMIT,
}: UseFixedQuestionsQuizOptions) {
  const [rawQs, setRawQs] = useState<any[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState<Record<number, number>>({});
  const [answerMeta, setAnswerMeta] = useState<Record<number, AnswerResult>>(
    {},
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);

  const finishCalledRef = useRef(false);
  const answeredQuestionsRef = useRef<Set<number>>(new Set());

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    return fetchQuestions(lang, limit, [])
      .then((res) => {
        const raw: any[] = res.data?.data || res.data || [];
        if (raw.length === 0) {
          setIsEmpty(true);
          setFinished(true);
        } else {
          const { shuffleQuestions } = getShuffleSettings();
          setRawQs(shuffleQuestions ? shuffleArray(raw) : raw);
        }
      })
      .catch((e: any) => {
        setError(e?.response?.data?.message || "Savollar yuklanmadi");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchQuestions, limit]);

  // ─── INIT ──────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    load().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
    // Lang o'zgarganda qayta yuklamaymiz — questions remap qilamiz
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── rawQs → questions mapping ─────────────────────────────────────
  useEffect(() => {
    if (!rawQs.length) return;
    setQuestions((prev) => mapRawQuestions(rawQs, lang, answerMeta, prev));
  }, [rawQs, lang, answerMeta]);

  // ─── Hisoblar ──────────────────────────────────────────────────────
  const correctCount = Object.keys(answered).filter(
    (k) => questions[Number(k)]?.correct_answer_index === answered[Number(k)],
  ).length;

  const wrongCount = Object.keys(answered).filter(
    (k) => questions[Number(k)]?.correct_answer_index !== answered[Number(k)],
  ).length;

  // ─── FINISH ─────────────────────────────────────────────────────────
  const finish = useCallback(() => {
    if (finishCalledRef.current) return;
    finishCalledRef.current = true;
    setFinished(true);
  }, []);

  // ─── ANSWER ─────────────────────────────────────────────────────────
  const handleAnswer = useCallback(
    (orderIndex: number) => {
      if (answeredQuestionsRef.current.has(current) || finished) return;
      const q = questions[current];
      if (!q) return;
      answeredQuestionsRef.current.add(current);

      const isCorrect = q.correct_answer_index === orderIndex;

      const newAnswered = { ...answered, [current]: orderIndex };
      setAnswered(newAnswered);
      setAnswerMeta((prev) => ({
        ...prev,
        [q.id]: {
          selectedIndex: orderIndex,
          isCorrect,
          correctAnswerIndex: q.correct_answer_index,
        },
      }));

      if (isCorrect && current < questions.length - 1) {
        setTimeout(() => {
          setCurrent((p) => Math.min(questions.length - 1, p + 1));
        }, AUTO_ADVANCE_DELAY);
      }

      if (Object.keys(newAnswered).length === questions.length) {
        setTimeout(() => finish(), AUTO_FINISH_DELAY);
      }
    },
    [answered, current, finished, questions, finish],
  );

  // ─── Reset ─────────────────────────────────────────────────────────
  const reset = useCallback(() => {
    finishCalledRef.current = false;
    answeredQuestionsRef.current = new Set();
    setRawQs([]);
    setQuestions([]);
    setAnswered({});
    setAnswerMeta({});
    setCurrent(0);
    setFinished(false);
    setIsEmpty(false);
    load();
  }, [load]);

  return {
    questions,
    current,
    answered,
    answerMeta,
    loading,
    error,
    finished,
    isEmpty,
    correctCount,
    wrongCount,
    handleAnswer,
    setCurrent,
    finish,
    reset,
  };
}
